import { Banknote, CreditCard, ShoppingCart, Undo2 } from 'lucide-react'
import { Fragment, useId, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react'

import { Button } from './Button'
import { formatAmount } from './money'
import { ProductThumb } from './PhotoViewer'
import './receipt-table.css'

const money = (value: number | null) => formatAmount({ rubles: value })

/** Вид оплаты чека по разбивке: обе части больше нуля — смешанная; разбивки нет — неизвестно, а не «наличные». */
export type ReceiptPaymentKind = 'cash' | 'card' | 'mixed' | 'unknown'
export function paymentKindOf(cash: number | null, card: number | null): ReceiptPaymentKind {
  if (cash === null || card === null) return 'unknown'
  if (cash > 0 && card > 0) return 'mixed'
  if (card > 0) return 'card'
  if (cash > 0) return 'cash'
  return 'unknown'
}
const PAYMENT_TEXT: Record<ReceiptPaymentKind, string> = { cash: 'Наличные', card: 'Карта', mixed: 'Смешанная', unknown: '—' }

/** Вид оплаты значком и словом (не только цветом); у смешанной в подсказке — обе части. */
export function ReceiptPaymentKindText({ cash, card }: { cash: number | null; card: number | null }) {
  const kind = paymentKindOf(cash, card)
  const title = kind === 'mixed' ? `Наличными ${money(cash)} · картой ${money(card)}` : kind === 'unknown' ? 'Разбивка оплаты не указана' : PAYMENT_TEXT[kind]
  return <span className="krp-receipts__pay" title={title}>
    {(kind === 'cash' || kind === 'mixed') && <Banknote size={14} aria-hidden="true" />}
    {(kind === 'card' || kind === 'mixed') && <CreditCard size={14} aria-hidden="true" />}
    {PAYMENT_TEXT[kind]}
  </span>
}

/** Нажатие пришло с кнопки или ссылки внутри строки («Подробнее», возврат, переход): у неё своё действие, строка не раскрывается. */
const fromControl = (target: EventTarget) => target instanceof Element && target.closest('button, a, input, select, textarea') !== null

/** Строка общей таблицы чеков: то, что есть у чека любого источника. Своё у источника — в `columns`. */
export interface ReceiptsTableRow {
  id: string
  refund: boolean
  /** Номер чека как у источника; пусто — «без номера». */
  number: string
  /** Время чека, уже в поясе отчёта. */
  at: string
  register: string
  cashier: string
  total: number | null
  cash: number | null
  card: number | null
  /** Чек не вошёл в выручку (не подтверждён, не проведён): строка приглушена, причина — в столбце источника. */
  muted?: boolean
  /** Вид документа словами в подсказке номера; не задано — «Приход» или «Возврат» по `refund` (чек коррекции называет свой тип расчёта). */
  kindLabel?: string
}

/** Свой столбец источника (состояние, смена, магазин): короткий текст в одну строку. */
export interface ReceiptsTableColumn<T> {
  id: string
  label: string
  cell: (row: T) => ReactNode
  /** Полный текст ячейки для подсказки, когда он обрезан многоточием. */
  title?: (row: T) => string
}

/** Источник чека в списке, где смешаны чеки нескольких источников: имя и цвет метки (тот же, что в легенде касс). */
export interface ReceiptsTableSource { name: string; color: string }

/**
 * Список чеков — ОДНА таблица на всё приложение (статистика магазина, вкладки «Чеки» источников, отчёт кассира, своя касса):
 * строка в одну линию, без зазоров; длинное обрезается многоточием, полное — в подсказке; таблица прокручивается вбок. Порядок
 * столбцов везде один: чек, время, оплата, сумма, источник (только где источников несколько), кассир, касса, затем свои столбцы
 * источника и последней — кнопка «Подробнее» (окно чека поверх экрана). Нажатие на строку (Enter, пробел) раскрывает под ней товары
 * чека; открыт один чек за раз, повторное нажатие сворачивает. Приход и возврат — значком, словом и оттенком строки. Без `renderLines`
 * строка не раскрывается, без `onDetails` нет «Подробнее»; без обоих (чеки коррекции 1С) строки только для чтения: не в порядке Tab.
 */
export function ReceiptsTable<T extends ReceiptsTableRow>({ label, rows, columns = [], source, renderLines, onDetails, focusId }: {
  label: string
  rows: readonly T[]
  columns?: readonly ReceiptsTableColumn<T>[]
  /** Столбец «Источник» — там, где в списке чеки нескольких источников; где источник один, столбца нет. */
  source?: (row: T) => ReceiptsTableSource
  /** Товары чека под раскрытой строкой; рисуются только у раскрытого чека, поэтому читается только он. Не задано — строки не раскрываются. */
  renderLines?: (row: T) => ReactNode
  /** Открыть окно чека с подробностями; не задано — окна у таких чеков нет, кнопки «Подробнее» нет. */
  onDetails?: (row: T) => void
  /** Чек, к которому прокрутить список (открытый по ссылке). */
  focusId?: string | null
}) {
  // Какой чек раскрыт — экранное состояние строки, а не намерение для адреса: в адрес не пишется и с экраном забывается.
  const [expanded, setExpanded] = useState<string | null>(null)
  const linesId = useId()
  const span = 7 + (source === undefined ? 0 : 1) + columns.length + (onDetails === undefined ? 0 : 1)
  /** Строка с товарами: в порядке Tab, раскрывается и сворачивается нажатием, Enter или пробелом. */
  const expander = (row: T, name: string, controls: string) => {
    const toggle = () => setExpanded((current) => current === row.id ? null : row.id)
    return {
      tabIndex: 0,
      'aria-label': `Товары чека: ${name}`,
      'aria-expanded': expanded === row.id,
      'aria-controls': controls,
      onClick: (event: MouseEvent<HTMLTableRowElement>) => { if (!fromControl(event.target)) toggle() },
      onKeyDown: (event: KeyboardEvent<HTMLTableRowElement>) => {
        if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) return
        event.preventDefault()
        toggle()
      },
    }
  }
  return <div className="krp-receipt-table-wrap">
    <table className="krp-receipts" aria-label={label}>
      <thead><tr>
        <th scope="col">Чек</th><th scope="col">Время</th><th scope="col">Оплата</th><th scope="col" className="krp-receipts__money">Сумма</th>
        {source !== undefined && <th scope="col">Источник</th>}
        <th scope="col">Кассир</th><th scope="col">Касса</th>
        {columns.map((column) => <th key={column.id} scope="col">{column.label}</th>)}
        {onDetails !== undefined && <th scope="col"><span className="krp-receipt-lines__sr">Подробнее</span></th>}
      </tr></thead>
      <tbody>{rows.map((row, index) => {
        const name = `${row.kindLabel ?? (row.refund ? 'Возврат' : 'Приход')} № ${row.number || 'без номера'}`
        const controls = `${linesId}-${index}`
        const origin = source?.(row)
        return <Fragment key={row.id}>
          <tr data-kind={row.refund ? 'refund' : 'sale'} data-muted={row.muted || undefined} {...(renderLines === undefined ? {} : expander(row, name, controls))}
            data-focus={row.id === focusId || undefined}
            ref={row.id === focusId ? (element) => element?.scrollIntoView({ block: 'center' }) : undefined}>
            <td title={name}><span className="krp-receipts__kind">
              {row.refund ? <Undo2 size={14} aria-hidden="true" /> : <ShoppingCart size={14} aria-hidden="true" />}{row.number || 'без номера'}
            </span></td>
            <td title={row.at}>{row.at}</td>
            <td><ReceiptPaymentKindText cash={row.cash} card={row.card} /></td>
            <td className="krp-receipts__money">{money(row.total)}</td>
            {origin !== undefined && <td title={origin.name}><span className="krp-receipt-table__source"><i style={{ backgroundColor: origin.color }} aria-hidden="true" />{origin.name}</span></td>}
            <td title={row.cashier}>{row.cashier}</td>
            <td title={row.register}>{row.register}</td>
            {columns.map((column) => <td key={column.id} title={column.title?.(row)}>{column.cell(row)}</td>)}
            {onDetails !== undefined && <td>
              <Button variant="ghost" data-size="sm" aria-label={`Подробнее: ${name}`} onClick={() => onDetails(row)}>Подробнее</Button>
            </td>}
          </tr>
          {renderLines !== undefined && expanded === row.id && <tr id={controls} className="krp-receipts__expansion">
            <td className="krp-receipts__lines" colSpan={span}>{renderLines(row)}</td>
          </tr>}
        </Fragment>
      })}</tbody>
    </table>
  </div>
}

/** Реквизит шапки чека: подпись слева, значение справа. */
export interface ReceiptFact { label: string; value: ReactNode }

/** Шапка окна чека — таблица «подпись — значение», без фраз. */
export function ReceiptFacts({ label, facts }: { label: string; facts: readonly ReceiptFact[] }) {
  return <table className="krp-receipt-facts" aria-label={label}>
    <tbody>{facts.map((fact) => <tr key={fact.label}><th scope="row">{fact.label}</th><td>{fact.value}</td></tr>)}</tbody>
  </table>
}

/**
 * Позиция чека. Фото и переход в карточку — только у позиции с ключом товара источника (`open` задан): по штрихкоду или имени
 * товар не угадывается (решение владельца 01.11.2026). Деньги и количества — уже текстом источника.
 */
export interface ReceiptLineRow {
  id: string
  name: string
  quantity: string
  price: string
  amount: string
  unit?: string | null
  discount?: string | null
  vat?: string | null
  barcode?: string | null
  /** Предмет расчёта, как записан в чеке. */
  subject?: string | null
  /** Сколько уже возвращено по позиции (у источника, который это знает). */
  returned?: string | null
  /** Миниатюра товара по ключу; `null` — фото нет (заглушка); не задано — у источника фото товаров нет, столбца фото нет. */
  photo?: string | null
  /** Открыть карточку товара источника — та же, что из номенклатуры. */
  open?: () => void
}

type Optional = 'unit' | 'discount' | 'vat' | 'subject' | 'barcode' | 'returned'
const OPTIONAL: readonly { key: Optional; label: string }[] = [
  { key: 'unit', label: 'Ед.' }, { key: 'discount', label: 'Скидка' }, { key: 'vat', label: 'НДС' }, { key: 'subject', label: 'Предмет' },
  { key: 'barcode', label: 'Штрихкод' }, { key: 'returned', label: 'Возвращено' },
]

/**
 * Товары чека — компактная таблица, как список номенклатуры: миниатюра и название-кнопка открывают ту же карточку товара. Столбец
 * показан, только если он заполнен хотя бы у одной позиции; ширина ограничена, длинное — многоточием, полное — в подсказке.
 */
export function ReceiptLinesTable({ lines }: { lines: readonly ReceiptLineRow[] }) {
  const optional = OPTIONAL.filter((column) => lines.some((line) => line[column.key] != null && line[column.key] !== ''))
  const thumbs = lines.some((line) => line.photo !== undefined)
  return <div className="krp-receipt-table-wrap">
    <table className="krp-receipt-lines" aria-label="Товары чека">
      <thead><tr>
        <th scope="col" className="krp-receipt-lines__no">№</th>
        {thumbs && <th scope="col"><span className="krp-receipt-lines__sr">Фото</span></th>}
        <th scope="col">Товар</th><th scope="col" className="krp-receipts__money">Кол-во</th>
        {optional.filter((column) => column.key === 'unit').map((column) => <th key={column.key} scope="col">{column.label}</th>)}
        <th scope="col" className="krp-receipts__money">Цена</th>
        {optional.filter((column) => column.key === 'discount').map((column) => <th key={column.key} scope="col" className="krp-receipts__money">{column.label}</th>)}
        <th scope="col" className="krp-receipts__money">Сумма</th>
        {optional.filter((column) => column.key !== 'unit' && column.key !== 'discount').map((column) => <th key={column.key} scope="col">{column.label}</th>)}
      </tr></thead>
      <tbody>{lines.map((line, index) => <tr key={line.id}>
        <td className="krp-receipt-lines__no">{index + 1}</td>
        {thumbs && <td className="krp-receipt-lines__thumb">{line.photo !== undefined && <ProductThumb src={line.photo} name={line.name} size={32} />}</td>}
        <td className="krp-receipt-lines__name" title={line.name}>
          {line.open === undefined ? line.name : <button type="button" className="krp-receipt-lines__open" onClick={line.open}>{line.name}</button>}
        </td>
        <td className="krp-receipts__money">{line.quantity}</td>
        {optional.filter((column) => column.key === 'unit').map((column) => <td key={column.key} title={line.unit ?? ''}>{line.unit ?? ''}</td>)}
        <td className="krp-receipts__money">{line.price}</td>
        {optional.filter((column) => column.key === 'discount').map((column) => <td key={column.key} className="krp-receipts__money">{line.discount ?? ''}</td>)}
        <td className="krp-receipts__money">{line.amount}</td>
        {optional.filter((column) => column.key !== 'unit' && column.key !== 'discount').map((column) =>
          <td key={column.key} title={line[column.key] ?? ''}>{line[column.key] ?? ''}</td>)}
      </tr>)}</tbody>
    </table>
  </div>
}
